import React, { useState, useRef, useEffect } from 'react';
import { X, UploadCloud, FileText, Check, AlertTriangle, Loader, Trash2 } from 'lucide-react';
import { auth } from '../../firebaseConfig';

const BulkNotificationModal = ({ isOpen, onClose, onUploadComplete }) => {
    const [files, setFiles] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [summary, setSummary] = useState(null);
    const fileInputRef = useRef(null);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setFiles([]);
            setIsProcessing(false);
            setProgress(0);
            setSummary(null);
        }
    }, [isOpen]);

    const handleFileSelect = (selectedFiles) => {
        const newFiles = Array.from(selectedFiles).map(file => ({
            id: Math.random().toString(36).substr(2, 9),
            file,
            status: 'pending', // pending, uploading, success, error
            message: ''
        }));
        setFiles(prev => [...prev, ...newFiles]);
        setSummary(null); // Reset summary if new files are added after a previous run
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isProcessing) return;

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const pdfFiles = Array.from(e.dataTransfer.files).filter(file => file.type === 'application/pdf');
            if (pdfFiles.length !== e.dataTransfer.files.length) {
                // Could show a toast warning that non-PDFs were ignored
            }
            handleFileSelect(pdfFiles);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const removeFile = (id) => {
        if (isProcessing) return;
        setFiles(prev => prev.filter(f => f.id !== id));
    };

    const processFiles = async () => {
        if (files.length === 0) return;

        setIsProcessing(true);
        setSummary(null);
        let completedCount = 0;
        let successCount = 0;
        let errorCount = 0;

        // Reset statuses for pending/error files before retrying?
        // Or just process 'pending' ones. Let's process all 'pending' or 'error' ones.
        // Actually, let's just process everything that isn't 'success'.

        const filesToProcess = files.filter(f => f.status !== 'success');
        const totalToProcess = filesToProcess.length;

        // Calculate initial progress based on already success files?
        // Let's simplify: progress bar tracks the current batch.

        for (let i = 0; i < filesToProcess.length; i++) {
            const fileObj = filesToProcess[i];

            // Update status to uploading
            setFiles(prev => prev.map(f => f.id === fileObj.id ? { ...f, status: 'uploading', message: '' } : f));

            try {
                const formData = new FormData();
                formData.append('file', fileObj.file);

                let token = null;
                if (auth.currentUser) {
                    token = await auth.currentUser.getIdToken();
                }

                const response = await fetch('/api/uploadNotification', {
                    method: 'POST',
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                    body: formData,
                });

                const result = await response.json();

                if (response.ok && result.success) {
                    setFiles(prev => prev.map(f => f.id === fileObj.id ? { ...f, status: 'success', message: 'Cadastrado com sucesso' } : f));
                    successCount++;
                } else {
                    let errorMessage = result.message || 'Erro desconhecido';
                    if (response.status === 409) errorMessage = 'Duplicado';

                    setFiles(prev => prev.map(f => f.id === fileObj.id ? { ...f, status: 'error', message: errorMessage } : f));
                    errorCount++;
                }
            } catch (error) {
                setFiles(prev => prev.map(f => f.id === fileObj.id ? { ...f, status: 'error', message: `Erro de rede: ${error.message}` } : f));
                errorCount++;
            }

            completedCount++;
            setProgress(Math.round((completedCount / totalToProcess) * 100));
        }

        setIsProcessing(false);
        setSummary({
            success: successCount,
            total: totalToProcess,
            errors: errorCount
        });

        if (successCount > 0 && onUploadComplete) {
            onUploadComplete(); // Trigger refresh in parent
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending': return <div className="w-5 h-5 rounded-full border-2 border-slate-300" />;
            case 'uploading': return <Loader className="w-5 h-5 text-blue-500 animate-spin" />;
            case 'success': return <Check className="w-5 h-5 text-green-500" />;
            case 'error': return <AlertTriangle className="w-5 h-5 text-red-500" />;
            default: return null;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1050] flex justify-center items-center p-4 animate-fade-in" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh] relative" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <header className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Cadastro em Massa</h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                            Arraste arquivos PDF para processar notificações automaticamente.
                        </p>
                    </div>
                    <button
                        onClick={!isProcessing ? onClose : undefined}
                        className={`p-2 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-slate-900 ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-100 dark:hover:bg-slate-700'}`} aria-label="Fechar"
                    >
                        <X size={24} className="text-slate-500" />
                    </button>
                </header>

                {/* Body */}
                <div className="p-6 overflow-y-auto flex-grow">

                    {/* Dropzone */}
                    <div
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onClick={() => !isProcessing && fileInputRef.current?.click()}
                        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer mb-6
                            ${isProcessing
                                ? 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50 cursor-not-allowed'
                                : 'border-blue-400 bg-blue-50 hover:bg-blue-100 dark:border-blue-800 dark:bg-slate-800 dark:hover:bg-slate-750'
                            }`}
                    >
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept=".pdf"
                            multiple
                            onChange={(e) => handleFileSelect(e.target.files)}
                            disabled={isProcessing}
                        />
                        <div className="flex flex-col items-center justify-center space-y-3">
                            <div className="p-4 bg-white dark:bg-slate-700 rounded-full shadow-sm">
                                <UploadCloud className="w-8 h-8 text-blue-500" />
                            </div>
                            <div>
                                <p className="text-lg font-medium text-slate-700 dark:text-slate-200">
                                    Clique para selecionar ou arraste arquivos aqui
                                </p>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                    Suporta apenas arquivos PDF
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <div className="mb-6">
                            <div className="flex justify-between text-sm mb-2 text-slate-600 dark:text-slate-300">
                                <span>Processando arquivos...</span>
                                <span>{progress}%</span>
                            </div>
                            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5">
                                <div
                                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                                    style={{ width: `${progress}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    {/* Summary Message */}
                    {summary && (
                        <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${summary.errors === 0 ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200' : 'bg-orange-50 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200'}`}>
                            {summary.errors === 0 ? <Check className="mt-0.5" /> : <AlertTriangle className="mt-0.5" />}
                            <div>
                                <p className="font-semibold">Processamento concluído</p>
                                <p className="text-sm mt-1">
                                    {summary.success} de {summary.total} notificações cadastradas com sucesso.
                                    {summary.errors > 0 && ` (${summary.errors} erros)`}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* File List */}
                    {files.length > 0 && (
                        <div className="space-y-3">
                            <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                Arquivos ({files.length})
                            </h3>
                            <ul className="space-y-2">
                                {files.map((item) => (
                                    <li key={item.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-100 dark:border-slate-700">
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <div className={`p-2 rounded-lg ${item.status === 'error' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'} dark:bg-slate-600 dark:text-slate-200`}>
                                                <FileText size={20} />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate max-w-[200px] sm:max-w-md">
                                                    {item.file.name}
                                                </p>
                                                {item.message && (
                                                    <p className={`text-xs truncate ${item.status === 'error' ? 'text-red-500' : 'text-green-500'}`}>
                                                        {item.message}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {getStatusIcon(item.status)}
                                            {!isProcessing && (
                                                <button
                                                    onClick={() => removeFile(item.id)}
                                                    className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-offset-slate-900" aria-label="Remover arquivo" title="Remover arquivo"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <footer className="p-6 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="btn-secondary"
                        disabled={isProcessing}
                    >
                        Fechar
                    </button>
                    <button
                        onClick={processFiles}
                        disabled={isProcessing || files.length === 0 || files.every(f => f.status === 'success')}
                        className="btn-primary flex items-center"
                    >
                        {isProcessing ? (
                            <>
                                <Loader className="animate-spin mr-2" size={18} />
                                Processando...
                            </>
                        ) : (
                            <>
                                <UploadCloud className="mr-2" size={18} />
                                {files.some(f => f.status === 'error') ? 'Tentar Novamente' : 'Iniciar Cadastro'}
                            </>
                        )}
                    </button>
                </footer>

            </div>
        </div>
    );
};

export default BulkNotificationModal;
